import { Control, ScaleLine, ZoomToExtent } from 'ol/control';
import Geocoder from 'ol-geocoder/dist/ol-geocoder';
import Overlay from 'ol/Overlay';
import { unByKey } from 'ol/Observable';
import { getArea, getLength } from 'ol/sphere';
import { LineString, Polygon } from 'ol/geom';
import MultiPoint from 'ol/geom/MultiPoint';
import {
  Style,
  Stroke,
  Fill,
  Circle,
  RegularShape,
  Text,
} from 'ol/style';
import {
  Image as ImageLayer,
} from 'ol/layer';
import { dom } from '@fortawesome/fontawesome-svg-core';

export default {
  data() {
    return {
      measureTooltip: null,
      measureListener: null,
      measureTooltipElement: null,
    };
  },
  methods: {
    createMeasurementTooltip(feature) {
      this.measureTooltipElement = document.createElement('div');
      this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
      this.measureTooltip = new Overlay({
        element: this.measureTooltipElement,
        offset: [0, -15],
        positioning: 'bottom-center',
      });
      this.map.addOverlay(this.measureTooltip);

      this.measureListener = feature.getGeometry().on('change', (evt) => {
        const geom = evt.target;
        let output = null;
        let tooltipCoord = null;
        if (geom instanceof Polygon) {
          output = `${Math.round(getArea(geom) * 0.000247105 * 10) / 10} Acres`;
          tooltipCoord = geom.getInteriorPoint().getCoordinates();
        } else if (geom instanceof LineString) {
          output = `${Math.round(getLength(geom) * 3.28084 * 10) / 10} Feet`;
          tooltipCoord = geom.getLastCoordinate();
        }
        this.measureTooltipElement.innerHTML = output;
        this.measureTooltip.setPosition(tooltipCoord);
      });
    },
    destroyMeasurementTooltip() {
      this.map.removeOverlay(this.measureTooltip);
      unByKey(this.measureListener);
      this.measureListener = null;
      this.measureTooltip = null;
    },
    createStyleFunction({ strokeColor, fillColor, lineDash }) {
      return (feature, viewRes, strokeWidth) => [
        new Style({
          stroke: new Stroke({
            color: strokeColor,
            width: strokeWidth || 3,
            lineDash,
          }),
          fill: new Fill({
            color: fillColor,
          }),
          text: new Text({
            text: feature.getId() ? feature.getId().toString() : '',
            font: '16px Calibri,sans-serif',
            overflow: true,
            fill: new Fill({
              color: '#000',
            }),
            stroke: new Stroke({
              color: '#fff',
              width: 3,
            }),
          }),
          image: new Circle({
            radius: 5,
            fill: new Fill({
              color: 'white',
            }),
            stroke: new Stroke({ color: strokeColor, width: 2 }),
          }),
        }),
      ];
    },
    createEditStyleFunction({ strokeColor, fillColor, lineDash }) {
      const styleFunction = this.createStyleFunction({ strokeColor, fillColor, lineDash });
      return (feature, viewRes) => [
        ...styleFunction(feature, viewRes, 4),
        new Style({
          image: new Circle({
            radius: 5,
            fill: new Fill({
              color: 'white',
            }),
            stroke: new Stroke({ color: strokeColor, width: 2 }),
          }),
          geometry: () => {
            const featureType = feature.getGeometry().getType();
            const coordinates = feature.getGeometry().getCoordinates();
            if (featureType === 'Polygon') return new MultiPoint(coordinates[0]);
            return new MultiPoint(coordinates);
          },
        }),
      ];
    },
    createPointStyleFunction({ strokeColor }) {
      return feature => [
        new Style({
          text: new Text({
            text: feature.getId() ? feature.getId().toString() : '',
            font: '16px Calibri,sans-serif',
            offsetY: -15,
            fill: new Fill({
              color: '#000',
            }),
            stroke: new Stroke({
              color: '#fff',
              width: 3,
            }),
          }),
          image: new RegularShape({
            points: 4,
            radius: 10,
            radius2: 0,
            angle: Math.PI / 4,
            stroke: new Stroke({ color: strokeColor, width: 3 }),
          }),
        }),
      ];
    },
    createPointEditStyleFunction({ strokeColor }) {
      const styleFunction = this.createPointStyleFunction({ strokeColor });
      return feature => [
        new Style({
          image: new RegularShape({
            points: 4,
            radius: 10,
            radius2: 0,
            angle: Math.PI / 4,
            stroke: new Stroke({ color: 'white', width: 6 }),
          }),
        }),
        ...styleFunction(feature),
      ];
    },
    createGeocoderControl() {
      const geocoder = new Geocoder('nominatim', {
        autoComplete: true,
        autoCompleteMinLength: 3,
        provider: 'osm',
        lang: 'en-US',
        placeholder: 'Search for a location',
        targetType: 'text-input',
        limit: 5,
        keepOpen: true,
        countrycodes: 'us',
        preventDefault: true,
      });

      geocoder.on('addresschosen', (evt) => {
        this.map.getView().animate({ zoom: 14, center: evt.coordinate });
      });

      return geocoder;
    },
    createLayerSwitcherControl() {
      // Check if it's a LUPC town - access from parent component or props
      let isLupcTown = false;
      // Option 1: Access from parent component (MappingForm)
      if (this.$parent && this.$parent.notification && this.$parent.notification.isLUPCTown !== undefined) {
        isLupcTown = this.$parent.notification.isLUPCTown;
      }

      // Option 2: Access from component props (if passed down)
      if (this.isLupcTown !== undefined) {
        isLupcTown = this.isLupcTown;
      }

      // Option 3: Access from Vuex store directly
      if (this.$store && this.$store.state.Notification && this.$store.state.Notification.notification) {
        isLupcTown = this.$store.state.Notification.notification.isLUPCTown;
      }
      const element = document.createElement('div');
      element.className = 'layer-control';

      const collapseTarget = 'layerControlCollapse';
      const contentClass = 'layer-content rounded m-0 mr-2 py-1 px-2 collapse';
      const contentElement = document.createElement('div');
      contentElement.className = contentClass;
      contentElement.id = collapseTarget;

      const collapseElement = document.createElement('button');
      collapseElement.className = 'btn py-2 w-100 w-lg-auto btn-primary rounded-pill btn-layer-control';
      collapseElement.dataset.toggle = 'collapse';
      collapseElement.dataset.target = `#${collapseTarget}`;
      collapseElement.setAttribute('type', 'button');
      collapseElement.setAttribute('aria-expanded', true);
      collapseElement.setAttribute('aria-controls', collapseTarget);
      collapseElement.setAttribute('aria-label', 'Show/hide map layers');
      collapseElement.addEventListener('click', () => {
        if (contentElement.className.includes('show')) {
          const overlay = document.getElementsByClassName('ol-overlaycontainer-stopevent')[0];
          overlay.style.zIndex = '0';
          element.className = 'layer-control';
          contentElement.className = contentClass;
          collapseElement.setAttribute('aria-expanded', false);
          const aboutMapBtnElement = document.getElementById('aboutMapBtn');
          if (aboutMapBtnElement) aboutMapBtnElement.classList.remove('low-z-index');
        } else {
          const overlay = document.getElementsByClassName('ol-overlaycontainer-stopevent')[0];
          overlay.style.zIndex = 'auto';
          element.className = 'layer-control show';
          contentElement.className = `${contentClass} show`;
          collapseElement.setAttribute('aria-expanded', true);
        }
      });

      const layersSpan = document.createElement('span');
      layersSpan.className = 'btn-layer-control-text';
      layersSpan.textContent = 'Layers ';
      collapseElement.appendChild(layersSpan);

      const collapseIcon = document.createElement('i');
      collapseIcon.className = 'fas fa-layer-group font-white';
      collapseElement.appendChild(collapseIcon);

      dom.i2svg({ node: collapseElement });

      const layers = this.map.getLayers().getArray();
      layers.filter(layer => layer.get('toggle'))
        .forEach((layer) => {
          contentElement.appendChild(this.createLayerGroupElement(layer, null, isLupcTown));
        });

      const titleElement = document.createElement('div');
      titleElement.className = 'layer-control-title d-flex justify-content-end font-weight-bold';
      titleElement.id = 'layerControlTitle';

      const btnElement = document.createElement('div');
      btnElement.className = 'layer-container d-flex justify-content-end ol-unselectable';
      btnElement.title = '';
      btnElement.appendChild(contentElement);
      btnElement.appendChild(collapseElement);

      element.appendChild(titleElement);
      element.appendChild(btnElement);

      return new Control({ element });
    },
    createLayerGroupElement(layer, parentLayerGroup, isLupcTown = false) {
      // Recursively drill down into layer groups
      if (layer.get('layers')) {
        const layerGroupElement = document.createElement('ul');
        layerGroupElement.className = 'layer-control-group mb-1 p-0';
        layerGroupElement.innerText = layer.get('title');
        layer.get('layers').getArray().forEach((l) => {
          layerGroupElement.appendChild(this.createLayerGroupElement(l, layer, isLupcTown));
        });
        return layerGroupElement;
      }
      return this.createLayerElement(layer, parentLayerGroup, isLupcTown);
    },
    createLayerElement(layer, parentLayerGroup, isLupcTown = false) {
      const layerTitle = layer.get('title');

      // Handle SWS layer visibility based on LUPC town status
      let shouldShowLayer = true;
      let layerVisibility = layer.getVisible();

      if (layerTitle && layerTitle.toLowerCase().includes('statewide standards')) {
        if (isLupcTown) {
          // LUPC town: show UT standards, hide regular standards
          if (layerTitle.toLowerCase().includes('- ut')) {
            layerVisibility = true;
            shouldShowLayer = true;
          } else if (!layerTitle.toLowerCase().includes('- ut')) {
            layerVisibility = false;
            shouldShowLayer = false;
          }
        } else {
          // Non-LUPC town: show regular standards, hide UT standards
          if (layerTitle.toLowerCase().includes('- ut')) {
            layerVisibility = false;
            shouldShowLayer = false;
          } else {
            layerVisibility = true;
            shouldShowLayer = true;
          }
        }

        // Set the layer visibility
        layer.setVisible(layerVisibility);
      } else if ((layerTitle && layerTitle.toLowerCase().includes('rivers')) || (layerTitle && layerTitle.toLowerCase().includes('lakes/ponds')) || (layerTitle && layerTitle.toLowerCase().includes('non forested wetlands'))) {
        if (isLupcTown) {
          shouldShowLayer = true;
        } else {
          shouldShowLayer = false;
        }
      }

      const element = document.createElement('li');
      element.className = 'layer-control-element form-check ml-2';
      element.id = layerTitle;

      // Hide the element if it shouldn't be shown
      if (!shouldShowLayer) {
        element.style.display = 'none';
      }

      const toggle = document.createElement('input');
      toggle.id = `toggle-${layer.ol_uid}`;
      toggle.type = parentLayerGroup.get('toggle');
      toggle.value = toggle.id;
      toggle.name = parentLayerGroup.get('title') || toggle.id;
      toggle.checked = layerVisibility;
      toggle.className = 'form-check-input';

      const label = document.createElement('label');
      label.setAttribute('for', toggle.id);
      label.innerText = layer.get('title');
      label.className = 'form-check-label';
      element.appendChild(toggle);
      element.appendChild(label);

      const legendLayers = layer.get('legendLayers');

      if (legendLayers && legendLayers.length) {
        let mapUrl = '';
        if (layer instanceof ImageLayer) {
          mapUrl = layer.getSource().getUrl();
        } else {
          [mapUrl] = [layer.getSource().getUrls()];
        }
        this.$http.get(`${mapUrl}/legend?f=json`)
          .then((response) => {
            if (!response.data.layers) { return; }
            const legendResponses = response.data.layers
              .filter(l => legendLayers.includes(l.layerId));
            const legendElement = this.createLegendElement(legendResponses, toggle);
            element.appendChild(legendElement);
          });
      }

      toggle.addEventListener('change', (event) => {
        if (parentLayerGroup.get('toggle') === 'radio') {
          parentLayerGroup.get('layers').forEach(l => l.setVisible(false));
        }
        layer.setVisible(event.target.checked);
      });

      return element;
    },
    createLegendElement(legendResponses, toggle) {
      const legendElement = document.createElement('ul');
      const legendCollapseClass = visible => `pl-1 font-sm collapse ${visible ? 'show' : ''}`;
      legendElement.className = legendCollapseClass(toggle.checked);
      toggle.addEventListener('change', () => {
        legendElement.className = legendCollapseClass(toggle.checked);
      });
      legendElement.style = 'list-style-type: none';
      legendResponses.forEach((lr) => {
        if (lr.legend.length > 1) {
          const legendLayerElement = document.createElement('ul');
          legendLayerElement.style = 'list-style-type: none';
          legendLayerElement.className = 'pl-1';

          lr.legend.forEach((l) => {
            const legendSymbolElement = document.createElement('li');
            legendSymbolElement.appendChild(this.createSymbolImageElement(l.imageData));

            const labelSpan = document.createElement('span');
            labelSpan.textContent = l.label;
            labelSpan.className = 'ml-2';
            legendSymbolElement.appendChild(labelSpan);

            legendLayerElement.appendChild(legendSymbolElement);
          });
          legendElement.appendChild(legendLayerElement);
        } else {
          const legendLayerElement = document.createElement('li');
          legendLayerElement.appendChild(this.createSymbolImageElement(lr.legend[0].imageData, '20px'));

          const labelSpan = document.createElement('span');
          labelSpan.textContent = lr.layerName;
          labelSpan.className = 'ml-2';
          legendLayerElement.appendChild(labelSpan);

          legendElement.appendChild(legendLayerElement);
        }
      });

      return legendElement;
    },
    createSymbolImageElement(imageData, width = null) {
      const symbolImageElement = document.createElement('img');
      symbolImageElement.className = 'ml-2';
      symbolImageElement.src = `data:image/jpeg;base64,${imageData}`;
      if (width) {
        symbolImageElement.style.width = width;
      }
      symbolImageElement.alt = 'layer icon';
      return symbolImageElement;
    },
    createScaleLineControl() {
      return new ScaleLine({
        units: 'imperial',
        bar: true,
        steps: 4,
        minWidth: 200,
      });
    },
    createCenterMapControl(centerExtent) {
      const labelElement = document.createElement('div');
      labelElement.className = 'btn btn-primary rounded-pill';
      const icon = document.createElement('i');
      icon.className = 'fas fa-expand font-white';
      labelElement.appendChild(icon);
      dom.i2svg({ node: labelElement });
      return new ZoomToExtent({
        extent: centerExtent,
        label: labelElement,
        tipLabel: 'Center Map',
      });
    },
  },
};
